import { isPlatformBrowser, Location } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { RawFileStoreService } from '@app/core/services/raw-file-store.service';
import { FileDropEntry } from '@app/shared/components/smash-file-drop/file-drop-entry';
import { FileSystemMetadata } from '@app/shared/components/smash-file-drop/interfaces';
import { isSafari } from '@app/shared/helpers/environment';
import { Identity } from '@app/shared/models/identity';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { GetRecordOutput } from '@smash-sdk/domain/ejs/01-2024';
import { lastValueFrom, Observable, Subscription } from 'rxjs';
import { first, take } from 'rxjs/operators';
import { Constant } from 'src/constant';
import * as fromRoot from '@app/reducers/index';
import * as fromUploader from '@app/client/uploader/store/reducers';
import { RawFileError } from '@app/client/uploader/interfaces/file';
import { DropboxActions, LayoutActions, UploadActions, WorkflowActions } from '@app/client/uploader/store/actions';
import { ThemeSimplified } from '@app/shared/helpers/theme';


@Component({
  selector: 'app-landing',
  templateUrl: './landing.component.html',
  styleUrls: ['./landing.component.scss'],
})
export class LandingComponent implements OnInit, OnDestroy {

  selectedLanguage$: Observable<any>;
  menuDisplayed$: Observable<boolean>;
  domain$: Observable<GetRecordOutput['record']>;
  rawFiles$: Observable<FileSystemMetadata[]>;
  rawFilesError$: Observable<RawFileError>;
  isFileOver$: Observable<boolean>;
  identity$: Observable<Identity>;
  dropboxCreating$: Observable<boolean>;
  dropboxCreating = false;
  uploaderMode$: Observable<GetRecordOutput['record']['options']['transferMode']['default']>;
  theme$: Observable<ThemeSimplified>;
  currentTooltipState: 'in' | 'out' = 'in';
  subscriptions: Subscription[] = [];
  isSafari: boolean;
  longpress: any;

  tooltipAnimate: NodeJS.Timeout;

  isOnDropboxRoute = false;

  constructor(
    private store$: Store<fromUploader.State & fromRoot.State>,
    private router: Router,
    private translateService: TranslateService,
    private route: ActivatedRoute,
    private location: Location,
    private rawFileStoreService: RawFileStoreService,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {
    this.domain$ = this.store$.select(fromRoot.getCurrentDomain);
    this.selectedLanguage$ = this.store$.select(fromRoot.getSelectedLanguage);
    this.rawFiles$ = this.store$.select(fromUploader.getAllRawFiles);
    this.isFileOver$ = this.store$.select(fromUploader.getIsFileOver);
    this.rawFilesError$ = this.store$.select(fromUploader.getRawFilesError);
    this.identity$ = this.store$.select(fromRoot.getActiveIdentity);
    this.uploaderMode$ = this.store$.select(fromUploader.getMode);
    this.dropboxCreating$ = this.store$.select(fromUploader.getDropboxCreating);
    this.theme$ = this.store$.select(fromRoot.getTheme);
  }


  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.store$.dispatch(UploadActions.ResetUpload());
      this.store$.dispatch(DropboxActions.ResetDropbox());
      this.isSafari = isSafari();
      this.startIntervalTooltipInfos();

      // TO DO find a better way ... because it catch routes for viewer :id
      if (this.route.snapshot.paramMap.get('dropboxId')) {
        this.isOnDropboxRoute = true;
        this.getDropbox();
      } else {
        this.redirectLangUrl();
      }
    }
  }

  private startIntervalTooltipInfos(): void {
    this.tooltipAnimate = setInterval(() => {
      this.currentTooltipState = this.currentTooltipState === 'in' ? 'out' : 'in';
    }, 3000);
  }

  getDropbox(): void {
    const id = this.route.snapshot.paramMap.get('dropboxId');
    this.store$.dispatch(DropboxActions.GetDropboxPreview({ dropboxId: id }));
  }

  redirectLangUrl() {
    const userLang = this.translateService.currentLang;
    if (userLang === Constant.languageFallback) {
      return;
    }

    this.location.go(userLang);
  }

  ngOnDestroy(): void {
    clearInterval(this.tooltipAnimate);
    this.subscriptions.map(subscription => subscription.unsubscribe());
  }

  fileOver() {
    this.store$.dispatch(LayoutActions.UpdateFileOver({ fileOver: true }));
  }

  onLongpressChanged(event: any) {
    this.longpress = event;
  }

  fileLeave() {
    this.store$.dispatch(LayoutActions.UpdateFileOver({ fileOver: false }));
  }

  async dropped(files: FileDropEntry[]) {
    const identity = await this.identity$.pipe(first(identity => !!identity)).toPromise();
    await this.rawFileStoreService.setFiles(files, identity);
    this.subscriptions.push(this.rawFiles$.subscribe(rawFiles => {
      if (rawFiles.length) {
        this.router.navigate(['prepare'], { relativeTo: this.route });
        this.store$.dispatch(WorkflowActions.updateStep({ step: 'PREPARE' }));
      }
    }));
  }

  async onSpinnerClick() {
    const uploaderMode$ = await this.uploaderMode$.pipe(first(mode => !!mode)).toPromise();
    if (uploaderMode$ === 'Dropbox') {
      await this.createTeamDropbox();
    }
    return;
  }

  async createTeamDropbox() {
    const domain = await lastValueFrom(this.domain$.pipe(take(1)));
    const dropboxCreating = await lastValueFrom(this.dropboxCreating$.pipe(take(1)));
    if (!dropboxCreating) {
      this.store$.dispatch(DropboxActions.CreateTeamDropbox({ teamId: domain.team }));
      const dropboxCreated = await this.store$.select(fromUploader.getDropboxCreated).pipe(first(dropboxCreated => !!dropboxCreated)).toPromise();
      if (dropboxCreated) {
        this.router.navigate(['request-created'], { relativeTo: this.route });
        this.store$.dispatch(WorkflowActions.updateStep({ step: 'REQUEST_CREATED' }));
      }
    }
  }
}
